/**
 * PPA Minimum Generation — target-vs-actual + shortfall tile.
 *
 * Tracks actual sent-out generation against the PPA's Minimum Expected
 * Generation (MEG) per Contract Year and shows the buyer's shortfall-payment
 * exposure. Unlike the per-interval pay-as-produced settlement, the MEG is an
 * annual floor — so this lives on its own tab.
 *
 * Data: GET /api/contracts/<id>/ppa/min-generation
 *   { guarantee:{meg, buyers_share_pct, ...}, by_year:[…], current, missing_inputs, caveats }
 */

import React, { useMemo } from 'react';
import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, Stack, Skeleton, Alert, AlertTitle, Chip,
  Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import axios from 'axios';

import KpiCard, { fmtAUDCompact } from './KpiCard';
import { defaultLegendProps } from './charts/shared';
import { useThemeColors } from '../theme/ThemeModeContext';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

const AMBER = '#D98A2B';
const STATUS = {
  on_target:        { color: '#22c55e', label: 'On target' },
  near_target:      { color: '#84cc16', label: 'Near target' },
  shortfall:        { color: '#f59e0b', label: 'Shortfall' },
  severe_shortfall: { color: '#ef4444', label: 'Severe' },
  unknown:          { color: '#94a3b8', label: 'No data' },
};

const gwh = (mwh) => (mwh == null ? null : +(mwh / 1000).toFixed(2));
const fmtGWh = (mwh) => (mwh == null ? '—' : `${(mwh / 1000).toLocaleString('en-AU', { maximumFractionDigits: 1 })} GWh`);
const fmtPct = (n) => (n == null ? '—' : `${n.toFixed(1)}%`);

export default function PpaMinGeneration({ contractId }) {
  const colors = useThemeColors();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API_BASE_URL}/api/contracts/${contractId}/ppa/min-generation`)
      .then((r) => { setData(r.data); setError(null); })
      .catch((e) => setError(e?.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [contractId]);

  useEffect(() => { load(); }, [load]);

  const g = data?.guarantee || {};
  const meg = g.min_expected_generation_mwh_per_year;
  const byYear = useMemo(() => data?.by_year || [], [data]);
  const cur = data?.current || null;
  const status = STATUS[cur?.status] || STATUS.unknown;

  const chart = useMemo(() => {
    const x = byYear.map((y) => `Yr ${y.year}`);
    const delivered = byYear.map((y) => gwh(y.delivered_mwh));
    const target = byYear.map(() => gwh(meg));
    return { x, delivered, target };
  }, [byYear, meg]);

  if (loading) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rounded" height={120} sx={{ bgcolor: colors.skeleton }} />
        <Skeleton variant="rounded" height={260} sx={{ bgcolor: colors.skeleton }} />
      </Stack>
    );
  }
  if (error) return <Alert severity="error">Couldn’t load minimum-generation tracking: {error}</Alert>;

  return (
    <Stack spacing={2.5}>
      {/* Header */}
      <Box>
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Minimum Generation</Typography>
          {meg != null && (
            <Chip size="small" label={`MEG ${fmtGWh(meg)}/yr`}
                  sx={{ bgcolor: 'rgba(217,138,43,0.12)', color: AMBER, fontWeight: 600 }} />
          )}
          {g.buyers_share_pct != null && (
            <Chip size="small" variant="outlined" label={`Buyer's share ${(g.buyers_share_pct * 100).toFixed(0)}%`} />
          )}
          {data?.counterparty && <Chip size="small" variant="outlined" label={data.counterparty} />}
        </Stack>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          Sent-out generation (+ deemed curtailment) vs the annual Minimum Expected Generation floor.
          {data?.cod && ` COD ${data.cod}${data.cod_source === 'inferred_first_generation' ? ' (inferred from first generation)' : ''}.`}
          {data?.region && ` Spot: ${data.region}.`}
          {g.curtailment_strike_aud_per_mwh != null && ` Curtailment strike $${g.curtailment_strike_aud_per_mwh}/MWh.`}
        </Typography>
      </Box>

      {/* Awaiting inputs */}
      {data?.missing_inputs?.length > 0 && (
        <Alert severity="info" variant="outlined">
          <AlertTitle>Awaiting inputs</AlertTitle>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {data.missing_inputs.map((m, i) => <li key={i}><Typography variant="body2">{m}</Typography></li>)}
          </Box>
        </Alert>
      )}

      {/* KPI strip — latest Contract Year */}
      {cur && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
          <KpiCard
            label={`Delivered — Yr ${cur.year}${cur.complete ? '' : ' (to date)'}`}
            value={fmtGWh(cur.delivered_mwh)}
            accent={status.color}
            tooltip={`Sent-out generation in Contract Year ${cur.year}.`}
          />
          <KpiCard
            label="% of annual MEG"
            value={fmtPct(cur.pct_of_target)}
            accent={status.color}
            tooltip={(cur.complete ? '(Delivered + deemed curtailment) ÷ MEG for the full year.'
              : `In-progress year. Projected full-year: ${fmtGWh(cur.projected_full_year_mwh)}.`)
              + ` Curtailment counted: ${fmtGWh(cur.curtailment_addback_mwh)}.`}
          />
          <KpiCard
            label={cur.complete ? 'Shortfall' : 'Shortfall vs pace'}
            value={fmtGWh(cur.shortfall_mwh)}
            accent={cur.shortfall_mwh > 0 ? '#ef4444' : '#22c55e'}
            tooltip={`Buyer's Electricity Shortfall Quantity: ${fmtGWh(cur.esq_mwh)} (share × LF × shortfall).`}
          />
          <KpiCard
            label="Shortfall payment"
            value={cur.shortfall_payment_aud == null ? '—' : fmtAUDCompact(cur.shortfall_payment_aud)}
            accent={cur.shortfall_payment_aud > 0 ? '#ef4444' : '#22c55e'}
            tooltip="ESQ × (QLD avg spot − Fixed × 65%)."
          />
        </Box>
      )}

      {/* Chart — delivered vs MEG per Contract Year */}
      <Card sx={{ p: 2.5 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
          Delivered vs MEG by Contract Year
        </Typography>
        {chart.x.length === 0 ? (
          <Box sx={{ py: 5, textAlign: 'center', color: 'text.disabled' }}>
            <Typography variant="body2">No generation data yet — set the COD and asset DUIDs to start tracking.</Typography>
          </Box>
        ) : (
          <BarChart
            height={300}
            xAxis={[{ scaleType: 'band', data: chart.x }]}
            yAxis={[{ valueFormatter: (v) => `${v} GWh` }]}
            series={[
              { data: chart.delivered, label: 'Delivered', color: AMBER },
              { data: chart.target, label: 'MEG target', color: '#94a3b8' },
            ]}
            margin={{ left: 56, right: 16, top: 16, bottom: 28 }}
            slotProps={{ legend: { ...defaultLegendProps } }}
          />
        )}
      </Card>

      {/* Per-year table */}
      {byYear.length > 0 && (
        <Card sx={{ p: 2.5 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>Contract years</Typography>
          <Table size="small" sx={{ mt: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell>Yr</TableCell><TableCell>Window</TableCell>
                <TableCell align="right">Delivered</TableCell>
                <TableCell align="right">+ Curt. (deemed)</TableCell>
                <TableCell align="right">MEG</TableCell>
                <TableCell align="right">% of MEG</TableCell>
                <TableCell align="right">Shortfall</TableCell>
                <TableCell align="right">Fixed $/MWh</TableCell>
                <TableCell align="right">QLD spot</TableCell>
                <TableCell align="right">Payment</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {byYear.map((y) => {
                const st = STATUS[y.status] || STATUS.unknown;
                return (
                  <TableRow key={y.year} hover>
                    <TableCell>{y.year}{y.complete ? '' : ' *'}</TableCell>
                    <TableCell>{y.start} → {y.end}</TableCell>
                    <TableCell align="right">{fmtGWh(y.delivered_mwh)}</TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary' }}>
                      {y.curtailment_addback_mwh ? `+${fmtGWh(y.curtailment_addback_mwh)}` : '—'}
                    </TableCell>
                    <TableCell align="right">{fmtGWh(y.target_mwh)}</TableCell>
                    <TableCell align="right">{fmtPct(y.pct_of_target)}</TableCell>
                    <TableCell align="right" sx={{ color: y.shortfall_mwh > 0 ? '#ef4444' : 'inherit' }}>
                      {fmtGWh(y.shortfall_mwh)}
                    </TableCell>
                    <TableCell align="right">{y.fixed_price_aud_per_mwh == null ? '—' : `$${y.fixed_price_aud_per_mwh}`}</TableCell>
                    <TableCell align="right">{y.qld_avg_spot_aud_per_mwh == null ? '—' : `$${y.qld_avg_spot_aud_per_mwh}`}</TableCell>
                    <TableCell align="right">{y.shortfall_payment_aud == null ? '—' : fmtAUDCompact(y.shortfall_payment_aud)}</TableCell>
                    <TableCell><Chip size="small" label={st.label}
                      sx={{ bgcolor: `${st.color}22`, color: st.color, fontWeight: 600 }} /></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            * in-progress year (status &amp; shortfall measured against elapsed-time pace).
          </Typography>
        </Card>
      )}

      {/* Caveats */}
      {data?.caveats?.length > 0 && (
        <Alert severity="warning" variant="outlined">
          <AlertTitle>Method notes</AlertTitle>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {data.caveats.map((c, i) => <li key={i}><Typography variant="body2">{c}</Typography></li>)}
          </Box>
        </Alert>
      )}
    </Stack>
  );
}
